import {create} from 'zustand';

/**
 * Bridge between CommunicationHub / AIAssistantChatPanel and the open whiteboard
 * editor so chart mutation plans can be applied via TLDraw when the chart is open,
 * and so entity chips in assistant answers can open EntityInfoDrawer.
 *
 * WhiteboardEditorWorkspace registers an applier (+ optional openEntity /
 * setEntityFilters / selectShapes) on mount; the AI panel reads `canApply` /
 * `applyMutations` / `openEntity` when the user confirms a ChartMutationPlan
 * or clicks an entity chip. Facet / autoselect lanes fill the extra slots.
 *
 * Pure LLM path (ticket 05+) also registers `applyMarkupMutations` for `<chart-op>`
 * markup without replacing the legacy ChartMutationPlan applier.
 */
const useChartAiBridgeStore = create((set, get) => ({
    /** Whiteboard id the registered applier belongs to. */
    chartId: null,
    /** On-chart entities from the open whiteboard editor (for AI workflows). */
    chartEntities: [],
    /** Undirected entity-to-entity edges from arrow bindings on the chart. */
    chartConnections: [],
    /** Free-floating text/note/geo annotations from the open whiteboard. */
    chartVisualAnnotations: [],
    /** On-chart pictures ≤1MB for Pure LLM vision context (no videos). */
    chartImages: [],
    /** @type {((plan: object) => Promise<object>)|null} */
    applyMutations: null,
    /** @type {((payload: object|string) => Promise<object>)|null} */
    applyMarkupMutations: null,
    /** @type {((entityRef: string) => void)|null} */
    openEntity: null,
    /** @type {((filters: object) => void)|null} Day 0 slot; Lane C fills the body. */
    setEntityFilters: null,
    /** @type {((payload: {entityRefs?: string[], arrowShapeIds?: string[]}) => void)|null} Day 0 slot; Lane D fills the body. */
    selectShapes: null,

    /**
     * @param {{
     *   chartId: string,
     *   applyMutations: (plan: object) => Promise<object>,
     *   applyMarkupMutations?: (payload: object|string) => Promise<object>,
     *   openEntity?: (entityRef: string) => void,
     *   setEntityFilters?: (filters: object) => void,
     *   selectShapes?: (payload: {entityRefs?: string[], arrowShapeIds?: string[]}) => void,
     * }} registration
     */
    registerApplier: ({
        chartId,
        applyMutations,
        applyMarkupMutations,
        openEntity,
        setEntityFilters,
        selectShapes,
    }) => {
        if (!chartId || typeof applyMutations !== 'function') {
            return;
        }
        set({
            chartId,
            applyMutations,
            applyMarkupMutations: typeof applyMarkupMutations === 'function'
                ? applyMarkupMutations
                : null,
            openEntity: typeof openEntity === 'function' ? openEntity : null,
            setEntityFilters: typeof setEntityFilters === 'function' ? setEntityFilters : null,
            selectShapes: typeof selectShapes === 'function' ? selectShapes : null,
        });
    },

    /**
     * Mirrors the live chart entity/connection snapshot for AI assistant workflows.
     *
     * @param {{
     *   chartId: string,
     *   chartEntities?: Array<object>,
     *   chartConnections?: Array<object>,
     *   chartVisualAnnotations?: Array<object>,
     *   chartImages?: Array<object>,
     * }} snapshot
     */
    setChartSnapshot: ({
        chartId,
        chartEntities = [],
        chartConnections = [],
        chartVisualAnnotations = [],
        chartImages = [],
    }) => {
        if (!chartId) {
            return;
        }
        const current = get();
        if (current.chartId && current.chartId !== chartId) {
            return;
        }
        set({
            chartId,
            chartEntities: Array.isArray(chartEntities) ? chartEntities : [],
            chartConnections: Array.isArray(chartConnections) ? chartConnections : [],
            chartVisualAnnotations: Array.isArray(chartVisualAnnotations) ? chartVisualAnnotations : [],
            chartImages: Array.isArray(chartImages) ? chartImages : [],
        });
    },

    /** Clears the applier only if it still belongs to `chartId` (avoids races on navigate). */
    unregisterApplier: (chartId) => {
        const current = get();
        if (chartId && current.chartId !== chartId) {
            return;
        }
        set({
            chartId: null,
            chartEntities: [],
            chartConnections: [],
            chartVisualAnnotations: [],
            chartImages: [],
            applyMutations: null,
            applyMarkupMutations: null,
            openEntity: null,
            setEntityFilters: null,
            selectShapes: null,
        });
    },

    /** True when an editor applier is registered for `planChartId` (or any chart if omitted). */
    canApply: (planChartId) => {
        const {chartId, applyMutations} = get();
        if (!chartId || typeof applyMutations !== 'function') {
            return false;
        }
        if (planChartId && planChartId !== chartId) {
            return false;
        }
        return true;
    },

    /**
     * True when a Pure LLM markup applier is registered for `planChartId`
     * (or any chart if omitted).
     */
    canApplyMarkup: (planChartId) => {
        const {chartId, applyMarkupMutations} = get();
        if (!chartId || typeof applyMarkupMutations !== 'function') {
            return false;
        }
        if (planChartId && planChartId !== chartId) {
            return false;
        }
        return true;
    },

    /** True when the open whiteboard can open EntityInfoDrawer for an entity ref. */
    canOpenEntity: () => typeof get().openEntity === 'function',

    /** True when the open whiteboard can apply entity facet filters. */
    canSetEntityFilters: () => typeof get().setEntityFilters === 'function',

    /** True when the open whiteboard can select cards/arrows from assistant mentions. */
    canSelectShapes: () => typeof get().selectShapes === 'function',
}));

export default useChartAiBridgeStore;
